import { z } from 'zod';
import { DISTRIBUTION_CADENCES } from '../calc/distribution.js';
import { PASSWORD_MIN_LENGTH_FLOOR } from './auth.js';
/**
 * Master Data & Configuration Center contracts (spec §13.5).
 *
 * This is the file that makes Rule 1 true rather than merely intended. Every
 * value QMS360 refuses to hardcode already lives in the database — targets, SLA
 * days, the priority matrix, the working week, the PQI weights — but until there
 * is a validated write path, *"an Org Admin changes this with no deploy"* is a
 * promise the product cannot keep, and a correction still means somebody with
 * `psql`, which Rule 9 forbids outright.
 *
 * Three validations below are load-bearing rather than cosmetic, and each is
 * marked where it appears: **weights must sum to 1**, **a working week cannot be
 * empty**, and **a benchmark's comparison direction is required**.
 */
const code = z
    .string()
    .trim()
    .min(1, 'A code is required.')
    .max(30)
    .regex(/^[A-Z0-9_-]+$/, 'Use capitals, digits, hyphens and underscores only.');
const name = z.string().trim().min(1, 'A name is required.').max(200);
const nameAr = z.string().trim().max(200).nullable().optional();
export const CONFIGURABLE_APPS = ['qaqc', 'lesson-learned', 'audit'];
// ---------------------------------------------------------------------------
// Business Unit Master
// ---------------------------------------------------------------------------
export const businessUnitCreateSchema = z.object({
    code,
    name,
    nameAr,
    headUserId: z.string().uuid().nullable().optional(),
});
export const businessUnitUpdateSchema = businessUnitCreateSchema.partial();
// ---------------------------------------------------------------------------
// Discipline Master
// ---------------------------------------------------------------------------
export const disciplineCreateSchema = z.object({
    code,
    name,
    nameAr,
    sortOrder: z.number().int().min(0).max(10000).optional(),
});
export const disciplineUpdateSchema = disciplineCreateSchema.partial();
// ---------------------------------------------------------------------------
// Categorisation & Risk Master
// ---------------------------------------------------------------------------
export const riskLevelSchema = z.enum(['low', 'medium', 'high']);
export const categorisationCreateSchema = z.object({
    /** Null means the entry feeds all three applications. */
    app: z.enum(CONFIGURABLE_APPS).nullable().optional(),
    category: z.string().trim().min(1, 'A category is required.').max(100),
    label: z.string().trim().min(1, 'A label is required.').max(200),
    labelAr: z.string().trim().max(200).nullable().optional(),
    riskLevel: riskLevelSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(10000).optional(),
});
export const categorisationUpdateSchema = categorisationCreateSchema.partial();
// ---------------------------------------------------------------------------
// Target Benchmarks
// ---------------------------------------------------------------------------
export const targetBenchmarkCreateSchema = z.object({
    app: z.enum(CONFIGURABLE_APPS),
    metricKey: z.string().trim().min(1, 'A metric key is required.').max(100),
    projectId: z.string().uuid().nullable().optional(),
    businessUnitId: z.string().uuid().nullable().optional(),
    targetValue: z.number().min(-1_000_000).max(1_000_000),
    /**
     * Which direction is good. `gte` for a closure rate, `lte` for an ageing
     * count — **required, never defaulted**. A target whose comparison is guessed
     * wrong reports success exactly when things are at their worst, and nothing
     * on the screen would look unusual.
     */
    comparison: z.enum(['gte', 'lte']),
});
export const targetBenchmarkUpdateSchema = targetBenchmarkCreateSchema
    .partial()
    .omit({ app: true, metricKey: true });
// ---------------------------------------------------------------------------
// PQI weighting (App 1, OPEN_QUESTIONS Q1)
// ---------------------------------------------------------------------------
export const PQI_CATEGORY_KEYS = [
    'externalNcr',
    'internalNcr',
    'rfi',
    'materialInspection',
];
const weightMap = z.object({
    externalNcr: z.number().min(0).max(1),
    internalNcr: z.number().min(0).max(1),
    rfi: z.number().min(0).max(1),
    materialInspection: z.number().min(0).max(1),
});
/**
 * A new weight set. There is deliberately **no update**.
 *
 * An approved PQI has to stay reproducible: a report approved in March was
 * calculated with March's weights, and silently rewriting what that figure meant
 * is exactly what ADR-0007 exists to prevent. So a change is a new version and
 * the old one keeps its rows.
 *
 * The four weights must sum to 1. Not "should" — a set summing to 0.9 produces
 * an index that still looks like a percentage, still sits in the right range,
 * and is wrong by a tenth. That is the most expensive kind of wrong, because
 * nobody notices.
 */
export const pqiWeightSetCreateSchema = z
    .object({
    name,
    weights: weightMap,
    ageingPenaltyPointsPerPct: z.number().min(0).max(100),
    ageingPenaltyCap: z.number().min(0).max(100),
    /**
     * Whether these numbers are still ours rather than Algihaz's. It drives the
     * "provisional" badge beside every PQI, so clearing it is a claim that the
     * client has confirmed them — and therefore its own deliberate act.
     */
    isProvisional: z.boolean(),
    makeDefault: z.boolean().optional(),
})
    .refine((value) => {
    const total = Object.values(value.weights).reduce((sum, weight) => sum + weight, 0);
    // Floating point: 0.1 + 0.2 + 0.3 + 0.4 is not exactly 1.
    return Math.abs(total - 1) < 1e-6;
}, { message: 'The four weights must add up to exactly 1.', path: ['weights'] });
// ---------------------------------------------------------------------------
// Escalation Matrix Configuration (spec §12, §13.5)
// ---------------------------------------------------------------------------
export const escalationLevelSchema = z.object({
    level: z.string().trim().min(1).max(10),
    afterDays: z.number().int().min(0).max(365),
    repeatEveryDays: z.number().int().min(0).max(365).optional(),
    /**
     * Role keys, resolved to people at send time — never user ids frozen into a
     * rule. A rule naming a person keeps escalating to them after they change job,
     * which is how an escalation ends up reaching nobody who can act on it.
     */
    recipientRoleKeys: z.array(z.string().trim().min(1).max(60)).max(20),
});
export const escalationRuleCreateSchema = z.object({
    app: z.enum(CONFIGURABLE_APPS),
    name,
    description: z.string().trim().max(2000).nullable().optional(),
    triggerEvent: z.string().trim().min(1, 'A trigger event is required.').max(80),
    entityType: z.string().trim().min(1, 'An entity type is required.').max(80),
    matchConditions: z.record(z.string(), z.unknown()).optional(),
    slaWorkingDays: z.number().int().min(0).max(365),
    reminderOffsetDays: z.array(z.number().int().min(0).max(365)).max(10).optional(),
    /**
     * Who holds the item while the clock runs — told at each reminder and at the
     * breach, alongside the person the clock runs against. For an approval clock
     * this is the approver role: the submitter cannot approve their own report.
     */
    reminderRecipientRoleKeys: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
    levels: z.array(escalationLevelSchema).max(10).optional(),
    priority: z.string().trim().max(10).nullable().optional(),
    upgradesToPriority: z.string().trim().max(10).nullable().optional(),
    isEnabled: z.boolean().optional(),
});
export const escalationRuleUpdateSchema = escalationRuleCreateSchema.partial().omit({ app: true });
// ---------------------------------------------------------------------------
// Distribution Lists
// ---------------------------------------------------------------------------
export const distributionListCreateSchema = z.object({
    app: z.enum(CONFIGURABLE_APPS),
    name,
    /**
     * Digest cadence, never per-update (spec §13.6) — or `on_approval`, for the
     * documents that go out when they are approved (the monthly report, the
     * annual programme's memo).
     */
    cadence: z.enum(DISTRIBUTION_CADENCES),
    /** Which report goes out. Required before a list can be switched on. */
    reportKey: z.string().trim().min(1).max(60).nullable().optional(),
    /** A fixed project filter; the dates are the period the run covers. */
    parameters: z.object({ projectId: z.string().uuid().optional() }).optional(),
    format: z.enum(['pdf', 'xlsx', 'csv']).optional(),
    /** Local time of day, in the organisation's timezone. */
    runTimeLocal: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time is HH:MM.')
        .optional(),
    recipientUserIds: z.array(z.string().uuid()).max(200).optional(),
    /** People in QMS360 are matched by email and get it in-app too; anyone else by email only. */
    recipientEmails: z.array(z.string().email()).max(200).optional(),
    isEnabled: z.boolean().optional(),
});
export const distributionListUpdateSchema = distributionListCreateSchema
    .partial()
    .omit({ app: true });
// ---------------------------------------------------------------------------
// Organisation settings and the working calendar
// ---------------------------------------------------------------------------
export const organizationSettingsSchema = z.object({
    name: name.optional(),
    defaultLocale: z.enum(['en', 'ar']).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    /**
     * The shortest password a local account may set. Bounded by the floor and by
     * bcrypt's 72-byte ceiling (see PASSWORD_MIN_LENGTH_FLOOR in schemas/auth).
     * 64, not 72: a minimum at the ceiling would leave no room for any
     * multi-byte character at all.
     */
    passwordMinLength: z
        .number()
        .int()
        .min(PASSWORD_MIN_LENGTH_FLOOR, `The minimum cannot be below ${PASSWORD_MIN_LENGTH_FLOOR}.`)
        .max(64, 'The minimum cannot be above 64.')
        .optional(),
    /**
     * How many months audit entries stay in the live log before operations may
     * archive them (spec §13.7). Null means no period is set — everything stays
     * online. Never a deletion: retention is archival (Rule 2), and the app itself
     * moves nothing. At least a year, so a financial year's evidence is always
     * searchable in place.
     */
    auditLogOnlineMonths: z
        .number()
        .int()
        .min(12, 'Keep at least 12 months online.')
        .max(600, 'That is longer than fifty years.')
        .nullable()
        .optional(),
    /** How far back a repeat nonconformity counts as a recurrence (reports). */
    ncRecurrenceWindowMonths: z
        .number()
        .int()
        .min(1, 'At least one month.')
        .max(60, 'At most five years.')
        .optional(),
    /** The ageing report's bands in days, ascending: [30, 60, 90]. */
    auditAgeingBuckets: z
        .array(z.number().int().min(1).max(3650))
        .min(1, 'Give at least one band.')
        .max(6, 'At most six bands.')
        .refine((days) => days.every((value, index) => index === 0 || value > days[index - 1]), {
        message: 'Each band must be longer than the one before.',
    })
        .optional(),
});
/**
 * The working week (Rule 1 — never assume Monday to Friday).
 *
 * ISO weekday numbers, 1 = Monday … 7 = Sunday. The Gulf working week is
 * Sunday–Thursday, which is `[7, 1, 2, 3, 4]`, and an SLA clock computed against
 * the wrong week is wrong in the direction that forgives lateness.
 *
 * An **empty** working week is refused. Every SLA clock in QMS360 counts working
 * days, so with no working days a clock can never elapse: nothing is ever late,
 * nothing escalates, and the system reports perfect compliance for ever.
 */
export const workingCalendarUpdateSchema = z.object({
    name: name.optional(),
    workingWeekdays: z
        .array(z.number().int().min(1).max(7))
        .min(1, 'A working week needs at least one working day, or no SLA clock can ever elapse.')
        .max(7)
        .optional(),
    timezone: z.string().trim().min(1).max(60).optional(),
});
export const holidayCreateSchema = z.object({
    holidayDate: z.string().date(),
    name: z.string().trim().min(1, 'Give the holiday a name.').max(200),
});
//# sourceMappingURL=master-data.js.map